// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import { firestore } from '../context/firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { subscribeToDeliveryBales } from '../shared_components/utils/FirebaseDeliveryUtils';

import BaleView from '../components/BaleView/BaleView.jsx';
import { DELIVERIES_ROUTE } from '../constant/routes.js';
import { BALE_DOCUMENT_PATH } from '../shared_components/utils/FirebaseUtils.js';
import { useAuthStore } from '../stores/authStore.js';

export default function Bale({
  activeBaleId,
  deliveryId,
  returnToDelivery,
  setActiveBaleId,
  showOnlyImported = true,
  filteredBaleIds = null,
}) {
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const [bales, setBales] = React.useState({});

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!activeBaleId) {
      navigate(DELIVERIES_ROUTE);
    }

    /* Subscribe to bale data */
    const unsubscribe = subscribeToDeliveryBales(
      firestore,
      currentUserClientId,
      deliveryId,
      setBales,
      showOnlyImported,
    );
    return unsubscribe;
  }, [activeBaleId]);

  // Filter bales based on filteredBaleIds if provided
  const filteredBales = React.useMemo(() => {
    if (!filteredBaleIds || filteredBaleIds.length === 0) {
      return bales;
    }
    const filtered = {};
    filteredBaleIds.forEach((baleId) => {
      if (bales[baleId]) {
        filtered[baleId] = bales[baleId];
      }
    });
    return filtered;
  }, [bales, filteredBaleIds]);

  function handleFieldChange(event) {
    /* Update properties of a bale */
    const { name, value } = event.target;
    const baleRef = doc(firestore, BALE_DOCUMENT_PATH(currentUserClientId, deliveryId, activeBaleId));
    updateDoc(baleRef, { [name]: value });
  }

  function getNextBale() {
    /* Get the next bale ID in the list of bales (with regards to the baleId) */
    const baleIds = Object.keys(filteredBales).sort();
    const currentIndex = baleIds.indexOf(activeBaleId);
    if (currentIndex === -1) return activeBaleId;

    const nextBaleId = currentIndex < baleIds.length - 1 ? baleIds[currentIndex + 1] : activeBaleId;
    setActiveBaleId(nextBaleId);
  }

  function getPreviousBale() {
    /* Get the previous bale ID in the list of bales (with regards to the baleId) */
    const baleIds = Object.keys(filteredBales).sort();
    const currentIndex = baleIds.indexOf(activeBaleId);
    if (currentIndex === -1) return activeBaleId;

    const previousBaleId = currentIndex > 0 ? baleIds[currentIndex - 1] : activeBaleId;
    setActiveBaleId(previousBaleId);
  }

  return (
    <BaleView
      bales={filteredBales}
      activeBaleId={activeBaleId}
      setActiveBaleId={setActiveBaleId}
      getNextBale={getNextBale}
      getPreviousBale={getPreviousBale}
      handleFieldChange={handleFieldChange}
      onCloseBaleView={returnToDelivery}
      activeBaleDocRef={doc(firestore, BALE_DOCUMENT_PATH(currentUserClientId, deliveryId, activeBaleId))}
    />
  );
}
